import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  BadgeIndianRupee,
  Clock,
  Edit3,
  Mail,
  MapPin,
  Maximize2,
  Minimize2,
  Save,
  Package,
  Phone,
  RefreshCw,
  Store,
  Users,
  UploadCloud,
  X,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  restaurantAdminService,
  rowsOf,
  unwrap,
} from "../../services/restaurantAdmin.service";
import { env } from "../../config/env";
import {
  isBase64Image,
  resolveRestaurantFoodImageUrl,
} from "../../utils/restaurantFoodImageUrl";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  LoadingBox,
  Input,
  Select,
  StatusPill,
  TableShell,
  Switch,
  Textarea,
  formatCurrency,
} from "./components/FoodAdminUi";

const MAX_RESTAURANT_IMAGE_SIZE = 3 * 1024 * 1024;

function valueOf(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "-";
}

function numberOf(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function boolOf(value, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const clean = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(clean)) return true;
  if (["false", "0", "no", "n"].includes(clean)) return false;
  return fallback;
}

function isRestaurantPermanentlyClosed(row = {}) {
  return boolOf(row?.isPermanentlyClosed ?? row?.is_permanently_closed, false);
}

function permanentCloseReasonOf(row = {}) {
  return row?.permanentCloseReason || row?.permanent_close_reason || "";
}

function permanentClosedAtOf(row = {}) {
  return row?.permanentlyClosedAt || row?.permanently_closed_at || "";
}

function restaurantName(row) {
  return row?.name || row?.restaurantName || row?.restaurant_name || "Restaurant";
}

function toPlainObject(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return {};
}

function rawImageValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return (
    value.url ||
    value.path ||
    value.fileUrl ||
    value.file_url ||
    value.preview ||
    value.secureUrl ||
    value.secure_url ||
    ""
  );
}

function uniqueStrings(values = []) {
  const seen = new Set();
  return values.filter((value) => {
    const clean = String(value || "").trim();
    if (!clean || seen.has(clean)) return false;
    seen.add(clean);
    return true;
  });
}

function imageUrlCandidates(value, { preferAuth = false } = {}) {
  const raw = String(rawImageValue(value) || "").trim();
  if (!raw) return [];
  if (isBase64Image(raw)) return [raw];
  if (/^https?:\/\//i.test(raw)) return [raw];
  if (raw.startsWith("//")) return [`${window.location.protocol}${raw}`];

  const clean = raw.replace(/^\/+/, "");
  const originalRelative = raw.startsWith("/") ? raw : `/${clean}`;
  const catalogBase = String(env.CATALOG_BASE_URL || "").replace(/\/$/, "");
  const authBase = String(env.AUTH_BASE_URL || "").replace(/\/$/, "");
  const restaurantAppBase = String(env.RESTAURANT_APP_BASE_URL || "").replace(/\/$/, "");

  const looksLikeAuthUpload = /(onboarding|restaurant[-_]?registration|registration[-_]?request|aadhaar|aadhar|pan|bank|passbook|kyc|document)/i.test(clean);
  const looksLikeCatalogUpload = /(restaurant[-_]?food|catalog|food[-_]?items?|menu)/i.test(clean);

  let bases;
  if (looksLikeCatalogUpload) bases = [catalogBase, restaurantAppBase, authBase];
  else if (preferAuth || looksLikeAuthUpload) bases = [authBase, restaurantAppBase, catalogBase];
  else bases = [restaurantAppBase, catalogBase, authBase];

  return uniqueStrings([
    ...bases.filter(Boolean).map((base) => `${base}/${clean}`),
    resolveRestaurantFoodImageUrl(originalRelative),
    originalRelative,
  ]);
}

function payloadOf(row) {
  return toPlainObject(row?.payload || row?.requestPayload || row?.request_payload || row?.registrationPayload || row?.registration_payload);
}

function documentsOf(row) {
  const payload = payloadOf(row);
  return {
    ...toPlainObject(payload.documents || payload.docs || payload.files),
    ...toPlainObject(row?.documents || row?.docs || row?.files),
  };
}

function pickDocument(docs, keys = []) {
  for (const key of keys) {
    if (docs?.[key]) return docs[key];
  }
  return "";
}

function collectImageCandidates(entries = []) {
  return uniqueStrings(
    entries.flatMap(({ value, preferAuth }) =>
      imageUrlCandidates(value, { preferAuth: Boolean(preferAuth) })
    )
  );
}

function restaurantLogoCandidates(row) {
  const payload = payloadOf(row);
  const docs = documentsOf(row);
  const logoDoc = pickDocument(docs, [
    "logo",
    "restaurantLogo",
    "restaurant_logo",
    "logoImage",
    "logo_image",
  ]);

  return collectImageCandidates([
    { value: row?.logoUrl || row?.logo_url || row?.imageUrl || row?.image_url },
    { value: payload.logoUrl || payload.logo_url || payload.imageUrl || payload.image_url },
    { value: logoDoc, preferAuth: true },
  ]);
}

function restaurantCoverCandidates(row) {
  const payload = payloadOf(row);
  const docs = documentsOf(row);
  const coverDoc = pickDocument(docs, [
    "cover",
    "coverImage",
    "cover_image",
    "restaurantCover",
    "restaurant_cover",
  ]);

  return collectImageCandidates([
    { value: row?.coverImageUrl || row?.cover_image_url || row?.coverUrl || row?.cover_url },
    { value: payload.coverImageUrl || payload.cover_image_url || payload.coverUrl || payload.cover_url },
    { value: coverDoc, preferAuth: true },
  ]);
}

function ImageWithFallback({ sources = [], alt = "", className = "", ...props }) {
  const safeSources = useMemo(() => uniqueStrings(sources), [sources]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [safeSources.join("|")]);

  if (!safeSources.length || !safeSources[index]) return null;

  return (
    <img
      {...props}
      src={safeSources[index]}
      alt={alt}
      className={className}
      onError={(event) => {
        if (index < safeSources.length - 1) {
          setIndex((current) => current + 1);
        } else {
          event.currentTarget.style.display = "none";
        }
      }}
    />
  );
}

function foodId(row) {
  return row?.foodItemId || row?.food_item_id || row?.id || row?._id || "";
}

function foodName(row) {
  return row?.name || row?.foodName || row?.food_name || "Food item";
}

function foodImage(row) {
  const raw =
    row?.imageUrl ||
    row?.image_url ||
    row?.variants?.[0]?.imageUrl ||
    row?.variants?.[0]?.image_url ||
    "";

  return raw && !isBase64Image(raw) ? resolveRestaurantFoodImageUrl(raw) : "";
}

function variantCount(row) {
  if (Array.isArray(row?.variants)) return row.variants.length;
  if (Array.isArray(row?.variantMappings)) return row.variantMappings.length;
  if (Array.isArray(row?.variant_mappings)) return row.variant_mappings.length;
  return Number(row?.variantCount || row?.variant_count || 0);
}

function permissionsOf(employee) {
  const raw =
    employee?.permissions ||
    employee?.permissionKeys ||
    employee?.permission_keys ||
    [];

  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return raw
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function DetailLine({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1.5 break-words text-[14px] font-semibold text-slate-800">
        {value || "-"}
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h3 className="mb-4 flex items-center gap-2 text-[19px] font-bold text-[#d86a78]">
      <Icon size={18} className="text-[#d86a78]" />
      {children}
    </h3>
  );
}

function TopActions({
  onBack,
  onRefresh,
  onEdit,
  onCancel,
  onSave,
  onPermanentCloseToggle,
  permanentlyClosed,
  isEditing,
  saving,
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <Button variant="ghost" onClick={onBack} disabled={saving}>
        <ArrowLeft size={16} />
        Back
      </Button>

      <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
        <Button variant="ghost" onClick={onRefresh} disabled={saving}>
          <RefreshCw size={16} />
          Refresh
        </Button>

        {!isEditing ? (
          <Button
            variant={permanentlyClosed ? "soft" : "danger"}
            onClick={onPermanentCloseToggle}
            disabled={saving}
          >
            {permanentlyClosed ? <RefreshCw size={16} /> : <XCircle size={16} />}
            {permanentlyClosed ? "Reopen Restaurant" : "Permanent Close"}
          </Button>
        ) : null}

        {isEditing ? (
          <>
            <Button variant="ghost" onClick={onCancel} disabled={saving}>
              <X size={16} />
              Cancel
            </Button>
            <Button onClick={onSave} disabled={saving}>
              <Save size={16} />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        ) : (
          <Button onClick={onEdit}>
            <Edit3 size={16} />
            Edit Restaurant
          </Button>
        )}
      </div>
    </div>
  );
}

function AvailabilityPill({ available }) {
  return (
    <span
      className={`inline-flex min-w-[96px] items-center justify-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase ${available
        ? "bg-emerald-50 text-emerald-700"
        : "bg-rose-50 text-rose-700"
        }`}
    >
      {available ? "Available" : "Not Available"}
    </span>
  );
}

function ImageFitControl({ value, onChange }) {
  return (
    <div className="absolute right-4 top-4 z-20 flex overflow-hidden rounded-[12px] border border-white/35 bg-black/35 p-1 shadow-lg backdrop-blur">
      <button
        type="button"
        onClick={() => onChange("cover")}
        className={`inline-flex items-center gap-1 rounded-[9px] px-3 py-2 text-[11px] font-semibold transition ${value === "cover"
          ? "bg-white text-[#d86a78]"
          : "text-white hover:bg-white/15"
          }`}
        title="Fill banner"
      >
        <Maximize2 size={13} />
        Fill
      </button>

      <button
        type="button"
        onClick={() => onChange("contain")}
        className={`inline-flex items-center gap-1 rounded-[9px] px-3 py-2 text-[11px] font-semibold transition ${value === "contain"
          ? "bg-white text-[#d86a78]"
          : "text-white hover:bg-white/15"
          }`}
        title="Fit full image"
      >
        <Minimize2 size={13} />
        Fit
      </button>
    </div>
  );
}


function ImageUploadField({ label, value, onUploaded }) {
  const inputId = React.useId();
  const preview = imageUrlCandidates(value)[0] || "";

  async function onFile(e) {
    const file = e.target.files?.[0];

    try {
      if (!file) return;
      if (!String(file.type || "").startsWith("image/")) throw new Error("Only image files are allowed");
      if (file.size > MAX_RESTAURANT_IMAGE_SIZE) throw new Error("Image must be 3 MB or less");

      const res = await restaurantAdminService.uploadFoodImage(file);
      const url = res?.data?.imageUrl || res?.data?.url || res?.imageUrl || res?.url;
      if (!url) throw new Error("Upload failed: image URL missing");

      onUploaded(url);
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div>
      <div className="mb-1 text-[12px] font-medium text-slate-500">{label}</div>
      <div className="flex min-h-[44px] items-center gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-2">
        {preview ? (
          <img src={preview} alt={label} className="h-9 w-9 rounded-lg object-cover" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-[#fff0f2]" />
        )}
        <div className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-500">
          {preview || "No image uploaded"}
        </div>
        <label
          htmlFor={inputId}
          className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-[#fff0f2] px-3 py-2 text-[11px] font-semibold text-[#d86a78]"
        >
          <UploadCloud size={13} />
          Upload
        </label>
        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={onFile} />
      </div>
    </div>
  );
}

const blankRestaurantForm = {
  code: "",
  name: "",
  phone: "",
  email: "",
  description: "",
  logoUrl: "",
  coverImageUrl: "",
  serviceRadiusKm: 5,
  minOrderValue: 99,
  packagingCharge: 0,
  adminCommissionPercent: 0,
  preparationTimeMins: 20,
  deliveryTimeMin: 30,
  deliveryTimeMax: 50,
  ratingAvg: 0,
  ratingCount: 0,
  isActive: true,
  isOpen: true,
  isPermanentlyClosed: false,
  permanentlyClosedAt: "",
  permanentlyClosedBy: "",
  permanentCloseReason: "",
  isFeatured: false,
  isRecommended: false,
  status: "APPROVED",
  address: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    lat: "",
    lng: "",
  },
};

function normalizeRestaurantForm(row = {}) {
  const address = row?.address || {};
  const payload = payloadOf(row);
  const docs = documentsOf(row);

  return {
    ...blankRestaurantForm,
    ...row,
    code: row?.code || "",
    name: row?.name || row?.restaurantName || row?.restaurant_name || "",
    phone: row?.phone || row?.ownerPhone || row?.owner_phone || "",
    email: row?.email || row?.ownerEmail || row?.owner_email || "",
    description: row?.description || "",
    logoUrl:
      row?.logoUrl ||
      row?.logo_url ||
      row?.imageUrl ||
      row?.image_url ||
      payload.logoUrl ||
      payload.logo_url ||
      rawImageValue(
        pickDocument(docs, ["logo", "restaurantLogo", "restaurant_logo", "logoImage", "logo_image"])
      ) ||
      "",
    coverImageUrl:
      row?.coverImageUrl ||
      row?.cover_image_url ||
      row?.coverUrl ||
      row?.cover_url ||
      payload.coverImageUrl ||
      payload.cover_image_url ||
      rawImageValue(
        pickDocument(docs, ["cover", "coverImage", "cover_image", "restaurantCover", "restaurant_cover"])
      ) ||
      "",
    serviceRadiusKm: row?.serviceRadiusKm ?? row?.service_radius_km ?? 5,
    minOrderValue: row?.minOrderValue ?? row?.min_order_value ?? 99,
    packagingCharge: row?.packagingCharge ?? row?.packaging_charge ?? 0,
    adminCommissionPercent:
      row?.adminCommissionPercent ?? row?.admin_commission_percent ?? row?.commissionPercent ?? 0,
    preparationTimeMins: row?.preparationTimeMins ?? row?.preparation_time_mins ?? 20,
    deliveryTimeMin: row?.deliveryTimeMin ?? row?.delivery_time_min ?? 30,
    deliveryTimeMax: row?.deliveryTimeMax ?? row?.delivery_time_max ?? 50,
    ratingAvg: row?.ratingAvg ?? row?.rating_avg ?? 0,
    ratingCount: row?.ratingCount ?? row?.rating_count ?? 0,
    isActive: boolOf(row?.isActive ?? row?.is_active, true),
    isOpen: boolOf(row?.isOpen ?? row?.is_open, true),
    isPermanentlyClosed: isRestaurantPermanentlyClosed(row),
    permanentlyClosedAt: permanentClosedAtOf(row),
    permanentlyClosedBy: row?.permanentlyClosedBy || row?.permanently_closed_by || "",
    permanentCloseReason: permanentCloseReasonOf(row),
    isFeatured: boolOf(row?.isFeatured ?? row?.is_featured, false),
    isRecommended: boolOf(row?.isRecommended ?? row?.is_recommended, false),
    status: row?.status || "APPROVED",
    address: {
      addressLine1:
        address?.addressLine1 || address?.address_line1 || row?.addressLine1 || row?.address_line1 || "",
      addressLine2:
        address?.addressLine2 || address?.address_line2 || row?.addressLine2 || row?.address_line2 || "",
      city: address?.city || row?.city || "",
      state: address?.state || row?.state || "",
      pincode: address?.pincode || row?.pincode || "",
      lat: address?.lat ?? address?.latitude ?? row?.lat ?? row?.latitude ?? "",
      lng: address?.lng ?? address?.longitude ?? row?.lng ?? row?.longitude ?? "",
    },
  };
}

function makeRestaurantPayload(form) {
  const {
    isPermanentlyClosed,
    is_permanently_closed,
    permanentlyClosedAt,
    permanently_closed_at,
    permanentlyClosedBy,
    permanently_closed_by,
    permanentCloseReason,
    permanent_close_reason,
    ...safeForm
  } = form || {};

  return {
    ...safeForm,
    serviceRadiusKm: Number(form.serviceRadiusKm || 0),
    minOrderValue: Number(form.minOrderValue || 0),
    packagingCharge: Number(form.packagingCharge || 0),
    adminCommissionPercent: Number(form.adminCommissionPercent || 0),
    preparationTimeMins: Number(form.preparationTimeMins || 0),
    deliveryTimeMin: Number(form.deliveryTimeMin || 0),
    deliveryTimeMax: Number(form.deliveryTimeMax || 0),
    ratingAvg: Number(form.ratingAvg || 0),
    ratingCount: Number(form.ratingCount || 0),
    address: {
      ...form.address,
      lat: Number(form.address?.lat || 0),
      lng: Number(form.address?.lng || 0),
    },
  };
}

function RestaurantEditCard({ form, setField, setAddress, onSave, onCancel, saving }) {
  return (
    <Card className="mb-5">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <SectionTitle icon={Edit3}>Edit restaurant details</SectionTitle>
          <p className="-mt-2 text-[12px] text-slate-500">
            Edit directly on the restaurant detail page. No separate list-page edit screen needed.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            <X size={15} />
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            <Save size={15} />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Input label="Code" value={form.code || ""} onChange={(e) => setField("code", e.target.value)} />
        <Input label="Name" value={form.name || ""} onChange={(e) => setField("name", e.target.value)} />
        <Input label="Phone" value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)} />
        <Input label="Email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} />
        <ImageUploadField label="Logo Image" value={form.logoUrl || ""} onUploaded={(url) => setField("logoUrl", url)} />
        <ImageUploadField label="Cover Image" value={form.coverImageUrl || ""} onUploaded={(url) => setField("coverImageUrl", url)} />
        <Input label="Address Line 1" value={form.address?.addressLine1 || ""} onChange={(e) => setAddress("addressLine1", e.target.value)} />
        <Input label="Address Line 2" value={form.address?.addressLine2 || ""} onChange={(e) => setAddress("addressLine2", e.target.value)} />
        <Input label="City" value={form.address?.city || ""} onChange={(e) => setAddress("city", e.target.value)} />
        <Input label="State" value={form.address?.state || ""} onChange={(e) => setAddress("state", e.target.value)} />
        <Input label="Pincode" value={form.address?.pincode || ""} onChange={(e) => setAddress("pincode", e.target.value)} />
        <Input label="Latitude" type="number" value={form.address?.lat || ""} onChange={(e) => setAddress("lat", e.target.value)} />
        <Input label="Longitude" type="number" value={form.address?.lng || ""} onChange={(e) => setAddress("lng", e.target.value)} />
        <Input label="Service Radius Km" type="number" value={form.serviceRadiusKm || ""} onChange={(e) => setField("serviceRadiusKm", e.target.value)} />
        <Input label="Min Order Value" type="number" value={form.minOrderValue || ""} onChange={(e) => setField("minOrderValue", e.target.value)} />
        <Input label="Packaging Charge" type="number" value={form.packagingCharge || ""} onChange={(e) => setField("packagingCharge", e.target.value)} />
        <Input label="Admin Cut %" type="number" min="0" max="100" value={form.adminCommissionPercent || ""} onChange={(e) => setField("adminCommissionPercent", e.target.value)} />
        <Input label="Preparation Time Mins" type="number" value={form.preparationTimeMins || ""} onChange={(e) => setField("preparationTimeMins", e.target.value)} />
        <Input label="Delivery Time Min" type="number" value={form.deliveryTimeMin || ""} onChange={(e) => setField("deliveryTimeMin", e.target.value)} />
        <Input label="Delivery Time Max" type="number" value={form.deliveryTimeMax || ""} onChange={(e) => setField("deliveryTimeMax", e.target.value)} />
        <Input label="Rating Avg" type="number" value={form.ratingAvg || ""} onChange={(e) => setField("ratingAvg", e.target.value)} />
        <Input label="Rating Count" type="number" value={form.ratingCount || ""} onChange={(e) => setField("ratingCount", e.target.value)} />
        <Select label="Status" value={form.status || "APPROVED"} onChange={(e) => setField("status", e.target.value)}>
          <option value="APPROVED">APPROVED</option>
          <option value="PENDING">PENDING</option>
          <option value="SUSPENDED">SUSPENDED</option>
          <option value="BLOCKED">BLOCKED</option>
        </Select>
      </div>

      <div className="mt-3">
        <Textarea label="Description" value={form.description || ""} onChange={(e) => setField("description", e.target.value)} />
      </div>

      {isRestaurantPermanentlyClosed(form) ? (
        <div className="mt-4 flex items-start gap-2 rounded-[14px] border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>This restaurant is permanently closed. Reopen it before changing Open or Active status.</span>
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        {[
          ["isActive", "Active"],
          ["isOpen", "Open"],
          ["isFeatured", "Featured"],
          ["isRecommended", "Recommended"],
        ].map(([key, label]) => (
          <div key={key} className="flex items-center justify-between rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] px-4 py-3">
            <span className="text-[13px] font-semibold text-slate-600">{label}</span>
            <Switch
              checked={Boolean(form[key])}
              onChange={(value) => setField(key, value)}
              disabled={isRestaurantPermanentlyClosed(form) && ["isActive", "isOpen"].includes(key)}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
export default function FoodRestaurantDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [restaurant, setRestaurant] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [foods, setFoods] = useState([]);
  const [coverFit, setCoverFit] = useState("cover");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(blankRestaurantForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const [restaurantRes, employeesRes, foodsRes] = await Promise.allSettled([
        restaurantAdminService.getRestaurant(id),
        restaurantAdminService.listRestaurantEmployees(id),
        restaurantAdminService.listRestaurantFoodItems(id, { limit: 500 }),
      ]);

      if (restaurantRes.status === "rejected") throw restaurantRes.reason;

      const nextRestaurant = unwrap(restaurantRes.value);
      setRestaurant(nextRestaurant);
      setForm(normalizeRestaurantForm(nextRestaurant));
      setEmployees(
        employeesRes.status === "fulfilled" ? rowsOf(employeesRes.value) : []
      );
      setFoods(foodsRes.status === "fulfilled" ? rowsOf(foodsRes.value) : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (searchParams.get("edit") === "1") {
      setIsEditing(true);
    }
  }, [searchParams]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setAddress(key, value) {
    setForm((prev) => ({
      ...prev,
      address: {
        ...(prev.address || {}),
        [key]: value,
      },
    }));
  }

  function startEdit() {
    setForm(normalizeRestaurantForm(restaurant));
    setIsEditing(true);
    setSearchParams({ edit: "1" });
  }

  function cancelEdit() {
    setForm(normalizeRestaurantForm(restaurant));
    setIsEditing(false);
    setSearchParams({});
  }

  async function saveRestaurant() {
    if (!form.name) {
      setError("Restaurant name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await restaurantAdminService.updateRestaurant(id, makeRestaurantPayload(form));
      setIsEditing(false);
      setSearchParams({});
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handlePermanentCloseToggle() {
    if (!restaurant || !id) return;

    const closed = isRestaurantPermanentlyClosed(restaurant);
    const name = restaurantName(restaurant);

    if (closed) {
      const confirmed = window.confirm(
        `Reopen ${name}? Restaurant employees will be able to login again after this.`
      );
      if (!confirmed) return;
    } else {
      const reason = window.prompt(
        `Reason for permanently closing ${name}? Restaurant employees will not be able to login.`,
        permanentCloseReasonOf(restaurant) || "Closed by admin"
      );
      if (reason === null) return;

      const confirmed = window.confirm(
        `Permanent close ${name}? This will block restaurant employee login.`
      );
      if (!confirmed) return;

      setSaving(true);
      setError("");
      try {
        await restaurantAdminService.setRestaurantPermanentClose(id, {
          isPermanentlyClosed: true,
          reason: String(reason || "Closed by admin").trim() || "Closed by admin",
        });
        await load();
      } catch (err) {
        setError(err);
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError("");
    try {
      await restaurantAdminService.setRestaurantPermanentClose(id, {
        isPermanentlyClosed: false,
        status: "APPROVED",
        isActive: true,
        isOpen: false,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const stats = useMemo(() => {
    const activeEmployees = employees.filter((employee) =>
      Boolean(employee.isActive ?? employee.is_active ?? employee.active ?? true)
    ).length;

    const availableFoods = foods.filter((food) =>
      Boolean(food.isAvailable ?? food.is_available ?? food.available ?? true)
    ).length;

    return { activeEmployees, availableFoods };
  }, [employees, foods]);

  if (loading) return <LoadingBox label="Loading restaurant details..." />;

  const visibleRestaurant = isEditing ? form : restaurant;
  const permanentlyClosed = isRestaurantPermanentlyClosed(visibleRestaurant);
  const permanentReason = permanentCloseReasonOf(visibleRestaurant);
  const address = visibleRestaurant?.address || {};
  const logoSources = restaurantLogoCandidates(visibleRestaurant);
  const coverSources = restaurantCoverCandidates(visibleRestaurant);

  const commission = numberOf(
    visibleRestaurant?.adminCommissionPercent ??
    visibleRestaurant?.admin_commission_percent ??
    visibleRestaurant?.commissionPercent,
    0
  );

  return (
    <div>
      <TopActions
        onBack={() => navigate("/admin/food-dashboard/restaurants")}
        onRefresh={load}
        onEdit={startEdit}
        onCancel={cancelEdit}
        onSave={saveRestaurant}
        onPermanentCloseToggle={handlePermanentCloseToggle}
        permanentlyClosed={permanentlyClosed}
        isEditing={isEditing}
        saving={saving}
      />

      <ErrorBox error={error} onRetry={load} />

      <Card className="mb-5 overflow-hidden p-0">
        <div className="relative h-[210px] overflow-hidden bg-[#fff1f3]">
          {coverSources.length ? (
            <>
              <ImageWithFallback
                sources={coverSources}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 blur-xl"
              />

              <ImageWithFallback
                sources={coverSources}
                alt="Cover"
                className={`relative z-10 h-full w-full ${coverFit === "cover" ? "object-cover" : "object-contain"
                  } object-center`}
              />

              <ImageFitControl value={coverFit} onChange={setCoverFit} />
            </>
          ) : null}

          <div className="absolute inset-0 z-10 bg-gradient-to-t from-slate-950/60 via-slate-950/15 to-transparent" />

          <div className="absolute bottom-5 left-5 right-5 z-20 flex items-end gap-4">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[22px] border-4 border-white bg-white shadow-lg">
              {logoSources.length ? (
                <ImageWithFallback sources={logoSources} alt="Logo" className="h-full w-full object-cover" />
              ) : (
                <Store size={38} className="text-[#d86a78]" />
              )}
            </div>

            <div className="min-w-0 pb-1 text-white">
              <h2 className="truncate text-[25px] font-bold">
                {restaurantName(visibleRestaurant)}
              </h2>

              <div className="mt-2 flex flex-wrap gap-2 text-[12px] font-semibold">
                <StatusPill value={visibleRestaurant?.status || "-"} />
                {permanentlyClosed ? <StatusPill value="Permanent Closed" /> : null}
                <StatusPill
                  value={boolOf(visibleRestaurant?.isOpen ?? visibleRestaurant?.is_open, false) ? "Open" : "Closed"}
                />
                <StatusPill
                  value={
                    boolOf(visibleRestaurant?.isActive ?? visibleRestaurant?.is_active, false)
                      ? "Active"
                      : "Inactive"
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {permanentlyClosed ? (
        <Card className="mb-5 border-rose-200 bg-rose-50">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-rose-100 text-rose-600">
                <XCircle size={18} />
              </div>
              <div>
                <div className="text-[15px] font-extrabold text-rose-700">Restaurant permanently closed</div>
                <p className="mt-1 text-[13px] font-medium leading-6 text-rose-700/90">
                  Restaurant employees cannot login. Customer/order availability should stay blocked until admin reopens it.
                </p>
                {permanentReason ? (
                  <p className="mt-1 text-[12px] font-semibold text-rose-700">Reason: {permanentReason}</p>
                ) : null}
              </div>
            </div>
            <Button variant="soft" onClick={handlePermanentCloseToggle} disabled={saving}>
              <RefreshCw size={15} />
              Reopen Restaurant
            </Button>
          </div>
        </Card>
      ) : null}

      {isEditing ? (
        <RestaurantEditCard
          form={form}
          setField={setField}
          setAddress={setAddress}
          onSave={saveRestaurant}
          onCancel={cancelEdit}
          saving={saving}
        />
      ) : null}

      <div className="mb-5 flex flex-col gap-5">
       
       <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SectionTitle icon={MapPin}>Address</SectionTitle>

            <div className="flex flex-wrap gap-2">
              {visibleRestaurant?.email ? (
                <a
                  href={`mailto:${restaurant.email}`}
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[#fff6f7] px-3 py-2 text-[12px] font-semibold text-[#d86a78]"
                >
                  <Mail size={14} />
                  Mail
                </a>
              ) : null}

              {visibleRestaurant?.phone ? (
                <a
                  href={`tel:${restaurant.phone}`}
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[#fff6f7] px-3 py-2 text-[12px] font-semibold text-[#d86a78]"
                >
                  <Phone size={14} />
                  Call
                </a>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 text-[14px] text-slate-600">
            <div className="flex min-w-[220px] flex-1 gap-2 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
              <MapPin size={16} className="mt-0.5 text-[#d86a78]" />
              <span>
                {valueOf(
                  address.addressLine1,
                  address.address_line1,
                  visibleRestaurant?.addressLine1,
                  visibleRestaurant?.address_line1
                )}
              </span>
            </div>

            <div className="min-w-[220px] flex-1 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
              {valueOf(
                address.addressLine2,
                address.address_line2,
                visibleRestaurant?.addressLine2,
                visibleRestaurant?.address_line2
              )}
            </div>

            <div className="min-w-[220px] flex-1 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
              {valueOf(address.city, visibleRestaurant?.city)},{" "}
              {valueOf(address.state, visibleRestaurant?.state)} -{" "}
              {valueOf(address.pincode, visibleRestaurant?.pincode)}
            </div>

            <div className="min-w-[260px] flex-1 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3 font-semibold text-slate-700">
              Lat/Lng: {valueOf(address.lat, visibleRestaurant?.lat)} /{" "}
              {valueOf(address.lng, visibleRestaurant?.lng)}
            </div>

          </div>
        </Card>
        <Card>
          <SectionTitle icon={Store}>Restaurant information</SectionTitle>

          <div className="flex flex-wrap gap-3 [&>*]:w-full md:[&>*]:w-[calc(50%-6px)] xl:[&>*]:w-[calc(33.333%-8px)]">
            <DetailLine label="Code" value={valueOf(visibleRestaurant?.code)} />
            <DetailLine label="Email" value={valueOf(visibleRestaurant?.email)} />
            <DetailLine label="Phone" value={valueOf(visibleRestaurant?.phone)} />
            <DetailLine
              label="Rating"
              value={`${valueOf(
                visibleRestaurant?.ratingAvg ?? visibleRestaurant?.rating_avg,
                0
              )} / ${valueOf(
                visibleRestaurant?.ratingCount ?? visibleRestaurant?.rating_count,
                0
              )} ratings`}
            />
            <DetailLine
              label="Min Order"
              value={formatCurrency(
                visibleRestaurant?.minOrderValue ?? visibleRestaurant?.min_order_value
              )}
            />
            <DetailLine
              label="Packaging Charge"
              value={formatCurrency(
                visibleRestaurant?.packagingCharge ?? visibleRestaurant?.packaging_charge
              )}
            />
            <DetailLine
              label="Service Radius"
              value={`${valueOf(
                visibleRestaurant?.serviceRadiusKm ?? visibleRestaurant?.service_radius_km,
                0
              )} km`}
            />
            <DetailLine
              label="Preparation Time"
              value={`${valueOf(
                visibleRestaurant?.preparationTimeMins ??
                visibleRestaurant?.preparation_time_mins,
                0
              )} mins`}
            />
            <DetailLine
              label="Delivery Time"
              value={`${valueOf(
                visibleRestaurant?.deliveryTimeMin ?? visibleRestaurant?.delivery_time_min,
                0
              )} - ${valueOf(
                visibleRestaurant?.deliveryTimeMax ?? visibleRestaurant?.delivery_time_max,
                0
              )} mins`}
            />
            <DetailLine label="Admin Cut" value={`${commission}%`} />
            <DetailLine label="Mapped Items" value={foods.length} />
            <DetailLine label="Employees" value={employees.length} />
            <DetailLine
              label="Available Food"
              value={stats.availableFoods}
            />
            <DetailLine
              label="Active Employees"
              value={stats.activeEmployees}
            />
            <DetailLine
              label="Created At"
              value={
                visibleRestaurant?.createdAt || visibleRestaurant?.created_at
                  ? new Date(
                    visibleRestaurant?.createdAt || visibleRestaurant?.created_at
                  ).toLocaleString()
                  : "-"
              }
            />
          </div>

          {visibleRestaurant?.description ? (
            <p className="mt-4 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-4 text-[14px] leading-relaxed text-slate-600">
              {restaurant.description}
            </p>
          ) : null}
        </Card>

        
      </div>

      <Card className="mb-5">
        <SectionTitle icon={Users}>Restaurant employees</SectionTitle>

        {employees.length ? (
          <div className="max-h-[230px] overflow-auto rounded-[16px] border border-[#eef0f4] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-white">
            <TableShell
              columns={["Name", "Contact", "Role", "Permissions", "Status", "Last Login"]}
              minWidth={950}
            >
              {employees.map((employee) => (
                <tr
                  key={employee.id || employee.employeeId || employee.employee_id}
                  className="border-b last:border-0"
                >
                  <td className="px-4 py-4 font-semibold text-slate-900">
                    {valueOf(employee.name, employee.fullName, employee.full_name)}
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-[14px] text-slate-700">
                      {valueOf(employee.email)}
                    </div>
                    <div className="mt-1 text-[12px] text-slate-400">
                      {valueOf(employee.phone)}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <StatusPill value={employee.role || "employee"} />
                  </td>

                  <td className="px-4 py-4">
                    <span className="rounded-full bg-[#fff6f7] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
                      {permissionsOf(employee).length} permissions
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <StatusPill
                      value={
                        employee.status ||
                        (employee.isActive ?? employee.is_active ? "Active" : "Inactive")
                      }
                    />
                  </td>

                  <td className="px-4 py-4 text-slate-500">
                    {employee.lastLoginAt || employee.last_login_at
                      ? new Date(
                        employee.lastLoginAt || employee.last_login_at
                      ).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </TableShell>
          </div>
        ) : (
          <EmptyState
            title="No employees found"
            subtitle="No owner/employee record came from auth-service for this restaurant."
            icon={<Users size={22} />}
          />
        )}
      </Card>

      <Card>
        <SectionTitle icon={Package}>Menu items sold by this restaurant</SectionTitle>

        {foods.length ? (
          <div className="max-h-[320px] overflow-auto rounded-[16px] border border-[#eef0f4] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <table className="w-full min-w-[980px] table-fixed text-left">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[13%]" />
                <col className="w-[8%]" />
                <col className="w-[16%]" />
                <col className="w-[15%]" />
                <col className="w-[8%]" />
                <col className="w-[10%]" />
              </colgroup>

              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-[#eef0f4]">
                  <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Food
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Category
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Variants
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Restaurant Price
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Availability
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Prep
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#eef0f4] bg-white">
                {foods.map((food) => {
                  const image = foodImage(food);
                  const idValue = foodId(food);
                  const available = Boolean(
                    food.isAvailable ?? food.is_available ?? food.available ?? true
                  );

                  return (
                    <tr
                      key={food.mappingId || food.mapping_id || idValue}
                      className="transition hover:bg-[#fffafb]"
                    >
                      <td className="px-4 py-4 align-middle">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-white">
                            {image ? (
                              <img
                                src={image}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Package size={18} className="text-[#d86a78]" />
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              idValue &&
                              navigate(`/admin/food-dashboard/items/${idValue}`)
                            }
                            className="min-w-0 truncate text-left text-[14px] font-semibold text-slate-900 hover:text-[#d86a78] hover:underline"
                            title={foodName(food)}
                          >
                            {foodName(food)}
                          </button>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <span
                          className="mx-auto block max-w-full truncate text-[14px] text-slate-600"
                          title={valueOf(food.categoryName, food.category_name, food.category)}
                        >
                          {valueOf(food.categoryName, food.category_name, food.category)}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-center align-middle text-[14px] text-slate-600">
                        {variantCount(food)}
                      </td>

                      <td className="px-4 py-4 text-center align-middle text-[14px] font-semibold text-slate-900">
                        <BadgeIndianRupee
                          size={14}
                          className="mr-1 inline text-[#d86a78]"
                        />
                        {formatCurrency(
                          food.restaurantPrice ??
                          food.restaurant_price ??
                          food.price ??
                          food.basePrice ??
                          food.base_price
                        )}
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <AvailabilityPill available={available} />
                        </div>
                      </td>

                      <td className="px-4 py-4 text-center align-middle text-[14px] text-slate-600">
                        <Clock size={14} className="mr-1 inline text-[#d86a78]" />
                        {valueOf(
                          food.preparationTimeMins,
                          food.preparation_time_mins,
                          visibleRestaurant?.preparationTimeMins,
                          visibleRestaurant?.preparation_time_mins
                        )}{" "}
                        mins
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              idValue &&
                              navigate(`/admin/food-dashboard/items/${idValue}`)
                            }
                          >
                            <span className="whitespace-nowrap">View Food</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No mapped items"
            subtitle="This restaurant is not selling any item yet."
            icon={<Package size={22} />}
          />
        )}
      </Card>
    </div>
  );
}
